import { Observable, Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { CommonService } from 'src/app/shared/common.service';
import { Cliente, SalesCurtainItemsResponse, SalesNewItemsResponse } from './sales.interfaces';
import type { Socket } from 'socket.io-client';
import { ReportHistory } from 'src/app/shared/common.enums';
import { AuthService } from 'src/app/auth/auth.service';

@Injectable({ providedIn: 'root' })
export class SalesService {
  user_id: string | number | undefined;
  public report$: Subject<ReportHistory[]> = new Subject<ReportHistory[]>();
  socket!: Socket;
  readonly socketReady: Promise<Socket>;

  constructor(
    private http: HttpClient,
    private commonService: CommonService,
    private authService: AuthService
  ) {
    this.user_id = this.authService.getUserFromLocalStorage()?.id;
    this.socketReady = this.initializeSocket();
  }

  private async initializeSocket(): Promise<Socket> {
    const { io } = await import('socket.io-client');
    const socket = io(environment.socketio, {
      auth: {
        id: this.user_id
      }
    });
    this.socket = socket;
    socket.on('reportHistory', (data) => {
      this.report$.next(data);
    });
    return socket;
  }

  //Reportes
  getReportesdaily(): Observable<ReportHistory[]> {
    return this.http.get<ReportHistory[]>(`${environment.endpoint}ventas/reportes/getReportesdaily`).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  getReportesfilter(data: any): Observable<ReportHistory[]> {
    return this.http.post<ReportHistory[]>(`${environment.endpoint}ventas/reportes/getReportesfilter`, data).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  generateExpiredReports(): Observable<any> {
    return this.http.post<any>(`${environment.endpoint}ventas/reportes/generateExpiredReports`, {}).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  getResumenEjecutivoVentas(data: { store: string, from: string, to: string }): Observable<{
    documents: any[],
    transactions: any[],
    payment_methods?: any[],
    services?: any[],
    saldos_a_favor?: any[],
    items?: any[]
  }> {
    return this.http.post<{
      documents: any[],
      transactions: any[],
      payment_methods?: any[],
      services?: any[],
      saldos_a_favor?: any[],
      items?: any[]
    }>(`${environment.endpoint}ventas/reportes/getResumenEjecutivoVentas`, data).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  getReportesdailySubject(): Observable<ReportHistory[]> {
    return this.report$.asObservable();
  }

  //utils
  public parseJson(data: any) {
    if (typeof data === 'string') {
      data = data.trim();
      if (data.startsWith('{') && !data.startsWith('[{' )) {
        data = '[' + data + ']';
      }
      try {
        return JSON.parse(data);
      } catch (error) {
        console.error('Error al parsear JSON:', error);
        return null;
      }
    }
    return data;
  }

  private hasFilterValue(value: any): boolean {
    if (value === null || value === undefined) {
      return false;
    }
    if (typeof value === 'string') {
      const normalizedValue = value.trim().toLowerCase();
      return normalizedValue !== '' && normalizedValue !== 'null' && normalizedValue !== 'undefined';
    }
    return true;
  }

  //Clientes
  reportFollowupExpired(followup_status_id: number): Observable<any> {
    return this.http.post<any>(
      `${environment.endpoint}ventas/clientes/report-followup-expired`,
      { followup_status_id }
    ).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  getClientes(
    limit: number,
    offset: number,
    sortField: string,
    sortOrder: string,
    filters: any
  ): Observable<{ clientes: Cliente[], total: number }> {
    const params: any = {
      limit: limit.toString(),
      offset: offset.toString(),
      sortField,
      sortOrder
    };
    // Agregar filtros al query
    for (const key in filters) {
      if (this.hasFilterValue(filters[key])) {
        params[`filter_${key}`] = filters[key];
      }
    }
    return this.http.get<{ clientes: Cliente[], total: number }>(
      `${environment.endpoint}ventas/clientes/get`,
      { params }
    ).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  getNewItems(
    limit: number,
    offset: number,
    sortField: string,
    sortOrder: string,
    filters: any
  ): Observable<SalesNewItemsResponse> {
    const params: any = {
      limit: limit.toString(),
      offset: offset.toString(),
      sortField,
      sortOrder
    };
    for (const key in filters) {
      params[`filter_${key}`] = filters[key];
    }
    return this.http.get<SalesNewItemsResponse>(`${environment.endpoint}ventas/new_items`, { params }).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  updateNewItemStatus(itemId: number, status: string): Observable<any> {
    return this.http.patch<any>(`${environment.endpoint}ventas/new_items/${itemId}/status`, { status }).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  getCurtainsConfirmationItems(
    page: number = 0,
    limit: number = 10,
    sortField: string = '',
    sortOrder: string = 'asc',
    filters: Record<string, any> = {}
  ): Observable<SalesCurtainItemsResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());
    if (sortField) {
      params = params.set('sortField', sortField);
    }
    if (sortOrder) {
      params = params.set('sortOrder', sortOrder);
    }
    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (value !== null && value !== undefined && value !== '') {
        params = params.set(key, value.toString());
      }
    });
    return this.http.get<SalesCurtainItemsResponse>(`${environment.endpoint}v2/purchase/cortinas/confirmacion`, { params }).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  saveCurtainNote(id: number, note: string): Observable<any> {
    return this.http.post<any>(`${environment.endpoint}v2/purchase/cortinas/confirmacion/${id}/nota`, { note }).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  confirmCurtain(id: number): Observable<any> {
    return this.http.post<any>(`${environment.endpoint}v2/purchase/cortinas/confirmacion/${id}/confirmar`, {}).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  searchClientes(search: string, getAll?: boolean): Observable<{ clientes: Cliente[], total: number }> {
    const params: any = {
      search: search.trim(),
      getAll: getAll ? 'true' : 'false'
    }
    return this.http.get<{ clientes: Cliente[], total: number }>(`${environment.endpoint}ventas/clientes/search`, { params }).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  getClienteById(id: number): Observable<any> {
    return this.http.get<any>(`${environment.endpoint}ventas/clientes/get/${id}`).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  createClient(data: any): Observable<any> {
    return this.http.post<any>(`${environment.endpoint}ventas/clientes/add`, data).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  editClient(data: any): Observable<any> {
    return this.http.put<any>(`${environment.endpoint}ventas/clientes/updateCliente`, data).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  deleteCliente(data: any): Observable<any> {
    return this.http.put<any>(`${environment.endpoint}ventas/clientes/delete`, data).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  addFollowingCliente(data: any): Observable<any> {
    return this.http.post<any>(`${environment.endpoint}ventas/clientes/addFollowingCliente`, data).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  addFollowingClientes(data: any): Observable<any> {
    return this.http.post<any>(`${environment.endpoint}ventas/clientes/addFollowingClientes`, data);
  }

  getHistorialByCliente(id: number): Observable<any> {
    return this.http.get<any>(`${environment.endpoint}ventas/clientes/getHistorialByCliente/${id}`).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  //Clientes/Seguimientos
  updateFollowUP(data: any): Observable<any> {
    return this.http.put<any>(`${environment.endpoint}ventas/clientes/updateFollowUP`, data).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  markFollowUP(data: any): Observable<any> {
    return this.http.put<any>(`${environment.endpoint}ventas/clientes/markFollowUP`, data).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  endFollowUP(data: any): Observable<any> {
    return this.http.put<any>(`${environment.endpoint}ventas/clientes/endFollowUP`, data).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  endFollowUP_manual(idFollowUP: number): Observable<any> {
    return this.http.post<any>(`${environment.endpoint}ventas/clientes/endFollowUP_manual`, { idFollowUP }).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  getPasobyFollowing(): Observable<any> {
    return this.http.get<any>(`${environment.endpoint}ventas/clientes/getPasobyFollowing`).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  /* sales config */
  get_config_common(tipo: string): Observable<any> {
    return this.http.get<any>(`${environment.endpoint}ventas/config/get/${tipo}`).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  add_config_common(tipo: string, data: any): Observable<any> {
    return this.http.post<any>(`${environment.endpoint}ventas/config/add/${tipo}`, data).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  update_config_common(tipo: string, data: any): Observable<any> {
    return this.http.put<any>(`${environment.endpoint}ventas/config/update/${tipo}`, data).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  // Seguimientos
  getSeguimientos(): Observable<any> {
    return this.http.get<any>(`${environment.endpoint}ventas/config/getSeguimientos`).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  getFrecuenciaBySeguimiento(id: number): Observable<any> {
    return this.http.get<any>(`${environment.endpoint}ventas/config/getFrecuenciaBySeguimiento/${id}`).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  editarFrecuencia(data: any): Observable<any> {
    return this.http.put<any>(`${environment.endpoint}ventas/config/updateFrecuencia`, data).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  getSeguimiento(id: number): Observable<any> {
    return this.http.get<any>(`${environment.endpoint}ventas/config/getSeguimiento/${id}`).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  AgregarSeguimiento(data: any): Observable<any> {
    return this.http.post<any>(`${environment.endpoint}ventas/config/addSeguimiento`, data).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  //Template/seguimiento/paso
  saveTemplate(data: any): Observable<any> {
    return this.http.put<any>(`${environment.endpoint}ventas/config/seguimiento/paso/template`, data).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  //Asistentes
  AgregarAsistente(data: any): Observable<any> {
    return this.http.post<any>(`${environment.endpoint}ventas/config/addAsistente`, data).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  getAsistentes(): Observable<any> {
    return this.http.get<any>(`${environment.endpoint}ventas/config/getAsistentes`).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  getAsistente(id: number): Observable<any> {
    return this.http.get<any>(`${environment.endpoint}ventas/config/getAsistente/${id}`).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  editarAsistente(data: any): Observable<any> {
    return this.http.put<any>(`${environment.endpoint}ventas/config/updateAsistente`, data).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  //Notas
  getNotasByCliente(id: number, limit?: number, offset?: number): Observable<any> {
    let params = new HttpParams();
    if (limit !== undefined) params = params.set('limit', limit.toString());
    if (offset !== undefined) params = params.set('offset', offset.toString());
    return this.http.get<any>(`${environment.endpoint}ventas/clientes/getNotas/${id}`, { params }).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  saveNote(data: any): Observable<any> {
    return this.http.post<any>(`${environment.endpoint}ventas/clientes/addNota`, data).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  deleteNota(id: number): Observable<any> {
    return this.http.delete<any>(`${environment.endpoint}ventas/clientes/deleteNota/${id}`).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  updateNoteClient(data: any): Observable<any> {
    return this.http.put<any>(`${environment.endpoint}ventas/clientes/updateNota`, data).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  getHistorialNota(id: number): Observable<any> {
    return this.http.get<any>(`${environment.endpoint}ventas/clientes/getHistorialNota/${id}`).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  //Mails
  getMailsToSend(id: number, step: number, idioma: string, idCliente: number, id_followup: number): Observable<any> {
    return this.http.get<any>(`${environment.endpoint}Mails/follow/getMailByStep/${id}/${step}/${idioma}/${idCliente}/${id_followup}`).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  SendFollowingMail(data: FormData): Observable<any> {
    return this.http.post<any>(`${environment.endpoint}Mails/send/following`, data).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  //Mails Promociones
  uploadPromoImage(formData: FormData): Observable<any> {
    return this.http.post<any>(`${environment.endpoint}Mails/promotions/upload`, formData).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  GetPromotions(): Observable<any> {
    return this.http.get<any>(`${environment.endpoint}Mails/promotions/get`).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  StatusPromotion(data: any): Observable<any> {
    return this.http.post<any>(`${environment.endpoint}Mails/promotions/status`, data).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  DeletePromotion(id: number, link: string): Observable<any> {
    const body = {
      id: id,
      link: link
    }
    return this.http.delete<any>(`${environment.endpoint}Mails/promotions/delete`, { body }).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  getMailById(id: number): Observable<any> {
    return this.http.get<any>(`${environment.endpoint}Mails/get/getMailById/${id}`).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  //Payment terms
  getPaymentTerms(): Observable<any> {
    return this.http.get<any>(`${environment.endpoint}ventas/config/getPaymentTerms`).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  getPaymentTermByStore(store: string, idioma?: string): Observable<any> {
    const params: { [param: string]: string } = {};
    if (idioma !== undefined) {
      params['idioma'] = idioma;
    }
    return this.http.get<any>(`${environment.endpoint}ventas/config/getPaymentTerm/${store}`, { params }).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  getPaymentTermById(id: number): Observable<any> {
    return this.http.get<any>(`${environment.endpoint}ventas/config/getPaymentTermById/${id}`).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  addPaymentTerm(data: any): Observable<any> {
    return this.http.post<any>(`${environment.endpoint}ventas/config/addPaymentTerm`, data).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  updatePaymentTerm(id: number, data: any): Observable<any> {
    return this.http.patch<any>(`${environment.endpoint}ventas/config/updatePaymentTerm/${id}`, data).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  updatePaymentTermStatus(id: number, status: string): Observable<any> {
    return this.http.patch<any>(`${environment.endpoint}ventas/config/updatePaymentTermStatus/${id}`, { status }).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }
}
